/* V36.25 — recommendations are verified against product metafields, never broadened. */
const textKey = value => String(value ?? '').normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');

export function familyPcd(value) {
  if (Array.isArray(value)) {
    const parts = value.map(familyPcd);
    return parts.length && parts.every(Boolean) ? [...new Set(parts.flatMap(part => part.split('/')))].sort().join('/') : '';
  }
  const text = textKey(value).replace(/[×✕]/g, 'x').replace(/\s/g, '').replace(/,/g, '.');
  const dual = text.match(/^(5|10)x(\d+(?:\.\d+)?)\/(?:5x)?(\d+(?:\.\d+)?)$/);
  if (dual) return [...new Set([`5x${Number(dual[2])}`, `5x${Number(dual[3])}`])].sort().join('/');
  const shorthand = text.match(/^(\d+)x(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/);
  if (shorthand && Number(shorthand[1]) >= 3) {
    return familyPcd(`${shorthand[1]}x${shorthand[2]}/${shorthand[1]}x${shorthand[3]}`);
  }
  const parts = text.split('/').map(part => part.match(/^(\d+)x(\d+(?:\.\d+)?)$/));
  if (parts.some(part => !part || Number(part[1]) < 3 || Number(part[2]) <= 0)) return '';
  return [...new Set(parts.map(part => `${Number(part[1])}x${Number(part[2])}`))].sort().join('/');
}

export function familyIdentity(data) {
  if (!data || typeof data !== 'object') return null;
  const brands = (Array.isArray(data.compatibility) ? data.compatibility : [data.compatibility])
    .filter(value => typeof value === 'string' && value.trim()).map(value => value.trim());
  const diameterText = textKey(data.diameter).replace(',', '.').replace(/\s*(pouces|["″])$/, '');
  const diameter = /^\d+(?:\.\d+)?$/.test(diameterText) ? Number(diameterText) : 0;
  const pcd = familyPcd(data.pcd);
  const model = textKey(data.model);
  const finish = textKey(data.finish);
  if (!data.id || !brands.length || !diameter || !pcd || !model || !finish) return null;
  return { id: String(data.id), brands, brandKeys: brands.map(textKey), diameter, pcd, model, finish };
}

export function sameFamily(current, candidate) {
  return Boolean(current && candidate && current.diameter === candidate.diameter && current.pcd === candidate.pcd &&
    current.brandKeys.some(brand => candidate.brandKeys.includes(brand)));
}

const modelFinishKey = identity => JSON.stringify([identity.model, identity.finish]);

export function selectFamilyItems(currentData, candidates, limit = 4) {
  const current = familyIdentity(currentData);
  if (!current) return [];
  const seenModels = new Set([modelFinishKey(current)]);
  const seenIds = new Set([current.id]);
  const result = [];
  for (const entry of candidates) {
    const candidate = familyIdentity(entry.data);
    if (!sameFamily(current, candidate) || seenIds.has(candidate.id) || seenModels.has(modelFinishKey(candidate))) continue;
    seenIds.add(candidate.id);
    seenModels.add(modelFinishKey(candidate));
    result.push(entry);
    if (result.length >= limit) break;
  }
  return result;
}

export function familyRequestURL(collectionURL, current, origin, previewTheme = '') {
  const url = new URL(collectionURL, origin);
  if (!collectionURL || url.origin !== origin || !/\/collections\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid family source');
  url.search = '';
  url.searchParams.set('section_id', 'directjantes-family-source');
  url.searchParams.set('filter.p.m.custom.compatibilite', current.brands.join(','));
  url.searchParams.set('filter.p.m.custom.diametre', String(current.diameter));
  url.searchParams.set('filter.p.m.custom.entraxes_compatibles', current.pcd.split('/').join(','));
  if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
  return url;
}

if (typeof customElements !== 'undefined' && !customElements.get('directjantes-family-recommendations')) {
  class FamilyRecommendations extends HTMLElement {
    #observer;
    #controller;
    #loading = false;

    connectedCallback() {
      if (this.dataset.state === 'ready' || this.#loading) return;
      this.#observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.#observer.disconnect();
        this.#load();
      }, { rootMargin: '600px 0px' });
      this.#observer.observe(this);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#controller?.abort();
      this.#loading = false;
    }

    async #load() {
      if (this.#loading) return;
      this.#loading = true;
      const controller = new AbortController();
      this.#controller = controller;
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const data = JSON.parse(this.dataset.wheel || 'null');
        const current = familyIdentity(data);
        if (!current || !this.dataset.collectionUrl) {
          this.dataset.state = 'empty';
          return;
        }
        const limit = Math.max(1, Math.min(10, Number(this.dataset.limit) || 4));
        const previewTheme = new URL(location.href).searchParams.get('preview_theme_id') || '';
        const url = familyRequestURL(this.dataset.collectionUrl, current, location.origin, previewTheme);
        const candidates = [];
        const visitedPages = new Set();
        let selected = [];
        // A family is normally small. Bound pathological duplicate catalogues and network work.
        for (let request = 0; request < 20; request++) {
          const key = url.href;
          if (visitedPages.has(key)) break;
          visitedPages.add(key);
          const response = await fetch(key, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Family source unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = document.querySelector('[data-dj-family-page]');
          if (!page || page.dataset.filterCount !== '3') throw new Error('Family filters unavailable');
          for (const template of page.querySelectorAll('template[data-dj-family-item]')) {
            try {
              const candidate = JSON.parse(template.dataset.wheel || 'null');
              if (template.content.querySelector('.dj-family-card')) candidates.push({ data: candidate, template });
            } catch { /* A malformed product must not broaden or break the selection. */ }
          }
          selected = selectFamilyItems(data, candidates, limit);
          if (selected.length >= limit || !page.dataset.next) break;
          const next = new URL(page.dataset.next, url);
          const pageNumber = next.searchParams.get('page');
          if (next.origin !== url.origin || !/^\d+$/.test(pageNumber || '') || Number(pageNumber) < 2) break;
          // Keep the original family filters and locale when moving to the next page.
          url.searchParams.set('page', pageNumber);
        }
        if (controller.signal.aborted || !this.isConnected) return;
        if (!selected.length) {
          this.dataset.state = 'empty';
          return;
        }
        const grid = this.querySelector('[data-dj-family-grid]');
        const content = this.querySelector('[data-dj-family-content]');
        if (!grid || !content) return;
        grid.replaceChildren(...selected.map(entry => document.importNode(entry.template.content, true)));
        content.hidden = false;
        this.dataset.state = 'ready';
      } catch {
        if (this.isConnected && this.#controller === controller) this.dataset.state = 'error';
      } finally {
        clearTimeout(timeout);
        if (this.#controller === controller) {
          this.#controller = null;
          this.#loading = false;
        }
      }
    }
  }
  customElements.define('directjantes-family-recommendations', FamilyRecommendations);
}
